/**
 * Frontend behavior for create-block/popup-form-block.
 *
 * - Opens the popup from the trigger button (fade + scale animation).
 * - Locks page scroll without layout shift while open.
 * - Popup closes ONLY via the X button (no overlay click, no Escape) —
 *   per design requirement.
 * - Traps keyboard focus inside the dialog while open.
 * - Auto-closes after a successful Contact Form 7 submission, once the
 *   success message has been visible long enough to read.
 */
(function () {
	"use strict";

	var SUCCESS_CLOSE_DELAY = 3000; // ms the CF7 success message stays visible.

	var openPopup = null;
	var lastTrigger = null;

	function getFocusable(dialog) {
		var nodes = dialog.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		return Array.prototype.filter.call(nodes, function (el) {
			return el.offsetParent !== null || el === document.activeElement;
		});
	}

	function lockScroll() {
		var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.overflow = "hidden";
		if (scrollbarWidth > 0) {
			document.body.style.paddingRight = scrollbarWidth + "px";
		}
	}

	function unlockScroll() {
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
	}

	function open(popup, trigger) {
		if (openPopup) {
			return;
		}
		openPopup = popup;
		lastTrigger = trigger || null;

		popup.hidden = false;
		lockScroll();

		// Next frame so the transition runs from the initial state.
		window.requestAnimationFrame(function () {
			window.requestAnimationFrame(function () {
				popup.classList.add("is-open");
			});
		});

		var dialog = popup.querySelector(".pfb-dialog");
		var closeBtn = popup.querySelector(".pfb-close");
		window.setTimeout(function () {
			(closeBtn || dialog).focus();
		}, getSpeed(popup));
	}

	function close(popup) {
		if (popup !== openPopup) {
			return;
		}
		popup.classList.remove("is-open");
		window.setTimeout(function () {
			popup.hidden = true;
			unlockScroll();
			openPopup = null;
			if (lastTrigger) {
				lastTrigger.focus();
				lastTrigger = null;
			}
		}, getSpeed(popup));
	}

	function getSpeed(popup) {
		var raw = window.getComputedStyle(popup).getPropertyValue("--pfb-speed");
		var speed = parseInt(raw, 10);
		return isNaN(speed) ? 350 : speed;
	}

	function init() {
		var popups = document.querySelectorAll("[data-pfb-popup]");
		if (!popups.length) {
			return;
		}

		// Popups are intentionally left in place (inside the block's own
		// section) rather than moved to <body> — page/theme CSS that styles
		// a specific form (card layouts, field spacing, button colors) is
		// often scoped to an ancestor selector, and relocating the popup
		// would silently drop that styling. position:fixed already renders
		// it above the rest of the page without needing to move it, unless
		// an ancestor sets transform/filter/perspective (uncommon for a
		// standalone block's own wrapper).
		popups.forEach(function (popup) {
			var closeBtn = popup.querySelector(".pfb-close");
			if (closeBtn) {
				closeBtn.addEventListener("click", function () {
					close(popup);
				});
			}
		});

		document.addEventListener("click", function (event) {
			var trigger = event.target.closest("[data-pfb-open]");
			if (!trigger) {
				return;
			}
			var popup = document.querySelector(
				'[data-pfb-popup="' + trigger.getAttribute("data-pfb-open") + '"]'
			);
			if (popup) {
				open(popup, trigger);
			}
		});

		// Focus trap. Escape is intentionally NOT handled — the popup must
		// only close via the X button (or auto-close after CF7 success).
		document.addEventListener("keydown", function (event) {
			if (!openPopup || event.key !== "Tab") {
				return;
			}
			var focusable = getFocusable(openPopup.querySelector(".pfb-dialog"));
			if (!focusable.length) {
				event.preventDefault();
				return;
			}
			var first = focusable[0];
			var last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			} else if (!openPopup.contains(document.activeElement)) {
				event.preventDefault();
				first.focus();
			}
		});

		// Auto-close after successful CF7 submission inside a popup.
		document.addEventListener("wpcf7mailsent", function (event) {
			if (!openPopup || !openPopup.contains(event.target)) {
				return;
			}
			var popup = openPopup;
			window.setTimeout(function () {
				close(popup);
			}, SUCCESS_CLOSE_DELAY);
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
